import { ExecMethod, ExecResp, InitMethod, InitResp } from 'src/database/types.ts';
import { DatabaseClientInterface } from 'shared/sql/DatabaseClientInterface';
import { QueryExecResult } from 'sql.js';
import DbWorker from 'src/database/worker?worker&inline';

export type CachedValue = QueryExecResult | '<NULL>';
export type Exec = (sql: string, params: string[]) => Promise<QueryExecResult[]>;

export class DatabaseClient implements DatabaseClientInterface {
    private workers: Worker[] = [];

    private pool: Exec[] = [];

    private lastPool = 0;

    public constructor(
        private cache: Map<string, CachedValue>,
        private readonly persistCallback: (cache: Map<string, CachedValue>) => Promise<void>,
    ) {
        Array.from({ length: 3 }).forEach(() => {
            const worker = new DbWorker();
            this.workers.push(worker);
            this.pool.push(
                (sql: string, params: string[]) => new Promise((resolve, reject) => {
                    const channel = new MessageChannel();
                    channel.port2.onmessage = (event: MessageEvent<ExecResp>) => {
                        channel.port2.close();
                        if ('error' in event.data) {
                            reject(event.data.error);
                        } else {
                            resolve(event.data.response);
                        }
                    };
                    worker.postMessage({ method: 'exec', params: [sql, params] } satisfies ExecMethod, [channel.port1]);
                }),
            );
        });
    }

    public async close() {
        this.workers.forEach((worker) => worker.terminate());
    }

    public async init(assetDomain: string) {
        await Promise.all(this.workers.map((worker) => new Promise((resolve, reject) => {
            const channel = new MessageChannel();
            channel.port2.onmessage = (event: MessageEvent<InitResp>) => {
                channel.port2.close();
                if ('error' in event.data) {
                    reject(event.data.error);
                } else {
                    resolve(event.data.response);
                }
            };
            worker.postMessage({ method: 'init', params: [assetDomain] } satisfies InitMethod, [channel.port1]);
        })));
    }

    public async raw(sql: string, params: string[]): Promise<QueryExecResult[]> {
        return this.getCallback()(sql, params);
    }

    public async getOne<T>(sql: string, params: string[], mapping: (item: unknown) => T): Promise<T | null> {
        const [product = null] = await this.getMany(sql, params, mapping);
        return product === null ? null : product;
    }

    public async getMany<T>(sql: string, params: string[], mapping: (item: unknown) => T): Promise<T[]> {
        const result = await this.executeWithCache(sql, params);
        if (result === null) {
            return [];
        }
        return result.values
            .map((values) => result.columns.reduce((acc, name, currentIndex) => ({
                ...acc,
                [name]: values[currentIndex],
            }), {}))
            .map<T>(mapping);
    }

    private getCallback() {
        const len = this.pool.length - 1;
        const current = this.pool[this.lastPool];
        if (this.lastPool === len) {
            this.lastPool = 0;
        } else {
            this.lastPool += 1;
        }
        return current;
    }

    protected async executeWithCache(
        sql: string,
        params: string[],
    ): Promise<QueryExecResult | null> {
        const key = JSON.stringify([sql, params]);
        // console.time(key);
        const fromCache = this.cache.get(key);
        if (fromCache != null) {
            // console.timeLog(key, 'cache');
            return fromCache === '<NULL>' ? null : fromCache;
        }
        const [result = null] = await this.getCallback()(sql, params);
        // console.timeLog(key);
        this.cache.set(key, result === null ? '<NULL>' : result);
        this.persistCallback(this.cache).then(() => null).catch(() => null);
        return result;
    }

    public length() {
        return this.workers.length;
    }
}
